const normalizeCurrency = (currency) => (currency ? String(currency).trim().toUpperCase() : null);

const toFiniteNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const firstFiniteNumber = (...values) => {
    for (const value of values) {
        const number = toFiniteNumber(value);
        if (number !== null) return number;
    }
    return null;
};

export const formatMoneyAmount = (amount) => {
    const number = toFiniteNumber(amount);
    if (number === null) return "—";
    return number.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export const formatCurrencyAmount = (amount, currency) => {
    const normalizedCurrency = normalizeCurrency(currency);
    return normalizedCurrency ? `${normalizedCurrency} ${formatMoneyAmount(amount)}` : formatMoneyAmount(amount);
};

export const formatDisplayRate = (rate) => {
    const number = toFiniteNumber(rate);
    if (number === null || number <= 0) return null;
    const maximumFractionDigits = number >= 1 ? 4 : 6;
    return number.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits,
    });
};

export const buildCurrencyConversionDisplay = (charge, fallbackBaseCurrency) => {
    if (!charge) return null;
    const originalCurrency = normalizeCurrency(charge.original_currency);
    const baseCurrency = normalizeCurrency(charge.base_currency || charge.currency || fallbackBaseCurrency);
    if (!originalCurrency || !baseCurrency || originalCurrency === baseCurrency) return null;

    const originalAmount = firstFiniteNumber(charge.original_amount, charge.slice_original_amount);
    const baseAmount = firstFiniteNumber(charge.base_amount, charge.amount, charge.slice_base_amount);
    const exchangeRate = toFiniteNumber(charge.exchange_rate);
    const displayRate = exchangeRate && exchangeRate > 0 ? 1 / exchangeRate : null;
    const displayRateText = formatDisplayRate(displayRate);
    const originalLabel = `原币 ${formatCurrencyAmount(originalAmount, originalCurrency)}`;
    const baseLabel = formatCurrencyAmount(baseAmount, baseCurrency);
    const rateLabel = displayRateText ? `1 ${baseCurrency} = ${displayRateText} ${originalCurrency}` : null;
    const formula = displayRateText && originalAmount !== null && baseAmount !== null
        ? `${formatCurrencyAmount(originalAmount, originalCurrency)} ÷ ${displayRateText} = ${formatCurrencyAmount(baseAmount, baseCurrency)}`
        : null;

    return {
        originalCurrency,
        baseCurrency,
        originalAmount,
        baseAmount,
        displayRate,
        displayRateText,
        originalLabel,
        baseLabel,
        rateLabel,
        formula,
        warning: charge.conversion_warning,
    };
};

export const summarizeOriginalCurrencyCharges = (charges, fallbackBaseCurrency, options = {}) => {
    const { includeReference = false } = options;
    const currentCharges = (charges || []).filter((charge) => charge.is_current_batch_charge);
    const scopedCharges = currentCharges.length > 0 || !includeReference
        ? currentCharges
        : (charges || []).filter((charge) => charge.is_reference_charge);
    const groups = new Map();

    scopedCharges.forEach((charge) => {
        const display = buildCurrencyConversionDisplay(charge, fallbackBaseCurrency);
        if (!display) return;

        const key = [display.originalCurrency, display.baseCurrency, display.displayRateText || "no-rate"].join("|");
        const existing = groups.get(key) || {
            originalCurrency: display.originalCurrency,
            baseCurrency: display.baseCurrency,
            displayRateText: display.displayRateText,
            originalAmount: 0,
            baseAmount: 0,
            hasBaseAmount: false,
            warnings: [],
        };

        if (display.originalAmount !== null) existing.originalAmount += display.originalAmount;
        if (display.baseAmount !== null) {
            existing.baseAmount += display.baseAmount;
            existing.hasBaseAmount = true;
        }
        if (display.warning) existing.warnings.push(display.warning);
        groups.set(key, existing);
    });

    const details = Array.from(groups.values()).map((group) => {
        const groupedDisplay = buildCurrencyConversionDisplay(
            {
                original_currency: group.originalCurrency,
                base_currency: group.baseCurrency,
                original_amount: group.originalAmount,
                base_amount: group.hasBaseAmount ? group.baseAmount : null,
                exchange_rate: group.displayRateText ? 1 / Number(group.displayRateText.replace(/,/g, "")) : null,
                conversion_warning: group.warnings[0],
            },
            fallbackBaseCurrency
        );
        return {
            ...group,
            originalLabel: `原币 ${formatCurrencyAmount(group.originalAmount, group.originalCurrency)}`,
            baseLabel: group.hasBaseAmount ? formatCurrencyAmount(group.baseAmount, group.baseCurrency) : null,
            rateLabel: group.displayRateText ? `1 ${group.baseCurrency} = ${group.displayRateText} ${group.originalCurrency}` : null,
            formula: groupedDisplay?.formula || null,
        };
    });

    if (details.length === 0) return null;

    const originalCurrencies = [...new Set(details.map((detail) => detail.originalCurrency))];
    const originalAmountTotal = details.length === 1 ? details[0].originalAmount : null;
    let label = "原币明细";
    if (originalCurrencies.length > 1) {
        label = "多币种";
    } else if (details.length === 1) {
        label = `原币 ${originalCurrencies[0]} ${formatMoneyAmount(originalAmountTotal)}`;
    }

    return {
        label,
        details,
        hasMultipleCurrencies: originalCurrencies.length > 1,
    };
};
